import axiosInstance from '../api';

export type VerdictKey =
  | 'below_comps'
  | 'priced_right'
  | 'slightly_high'
  | 'above_comps';

export type VerdictTone = 'primary' | 'success' | 'warn' | 'danger';

/** Mirrors the MLS-level status a listing carries at analysis time. */
export type MarketAnalysisListingStatus =
  | 'ACTIVE'
  | 'PENDING'
  | 'SOLD'
  | 'EXPIRED'
  | 'WITHDRAWN'
  | 'UNKNOWN';

export type MarketAnalysisStatKey =
  | 'daysOnMarket'
  | 'listToSaleRatio'
  | 'priceCuts'
  | 'monthsOfSupply';

export interface ValueBand {
  low: number;
  mid: number;
  high: number;
}

export interface MarketAnalysisVerdict {
  key: VerdictKey;
  tone: VerdictTone;
  title: string;
  sentence: string;
}

export interface MarketAnalysisStat {
  key: MarketAnalysisStatKey;
  value: string;
  label: string;
  detail: string;
}

export interface MarketAnalysisComp {
  id: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  beds: number;
  baths: number;
  area: number;
  soldPrice: number;
  adjustedPrice: number;
  soldOn: string | null;
  soldDaysAgo: number | null;
}

export interface MarketAnalysisProperty {
  address: string;
  city: string;
  state: string;
  zip: string;
  listPrice: number;
  beds: number;
  baths: number;
  area: number;
  yearBuilt: number | null;
  lotSizeAcres: number | null;
  daysOnMarket: number;
  photoUrl: string | null;
}

export interface OfferGuidance {
  low: number;
  high: number;
  suggested: number;
  text: string;
  /**
   * True when the list price already sits at or under what the comps
   * support -- `text` is then a single-price sentence rather than a range,
   * since a "$375K-$375K" range would read as a bug, not a read.
   */
  atList: boolean;
}

/**
 * Which geography the stat tiles describe -- the homes within N miles of this
 * one, the curated neighborhood it sits in, or the raw ZIP when neither holds
 * enough activity. Mirrors `MarketDataArea` on the market-data service.
 */
export interface MarketAnalysisArea {
  kind: 'neighborhood' | 'zip' | 'radius';
  slug: string | null;
  /** Display name: "within 3 miles", "Athens", or "35613". */
  name: string;
  attribution: string | null;
  matchedBy?: string;
  /** The radius that answered, after any widening. Radius scope only. */
  radiusMiles?: number;
}

export interface MarketAnalysis {
  listingId: string;
  /** False when there are too few sold comps to stand behind an estimate. */
  available: boolean;
  unavailableReason: 'NO_COMPS' | 'COMPS_DISAGREE' | null;
  property: MarketAnalysisProperty;
  /**
   * The listing's status at analysis time. Non-ACTIVE statuses carry a
   * non-null `statusNote` and never carry `offerGuidance` -- there is
   * nothing to offer on a home that already sold or is under contract.
   */
  listingStatus: MarketAnalysisListingStatus;
  /** The MLS's own status string, verbatim; displayed in place of a listingStatus label. */
  mlsStatus?: string | null;
  statusNote: string | null;
  valueBand: ValueBand | null;
  verdict: MarketAnalysisVerdict | null;
  /** Empty when the market service has no data for this area. */
  stats: MarketAnalysisStat[];
  marketDataAvailable: boolean;
  /** Null when there is no market data at all. */
  area: MarketAnalysisArea | null;
  comps: MarketAnalysisComp[];
  offerGuidance: OfferGuidance | null;
  sources: string;
  generatedAt: string;
}

const marketAnalysisService = {
  find: async (listingId: string): Promise<MarketAnalysis> => {
    const response = await axiosInstance.get<MarketAnalysis>(
      '/market-analysis',
      {
        params: { listingId },
      }
    );
    return response.data;
  },
};

export default marketAnalysisService;
